import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { AlertTriangle, Camera, Diamond, FlaskConical, Skull, Shield, TrendingDown, Send, Loader2, X, Undo2, Eye, EyeOff, Crosshair, Zap, Flag } from "lucide-react";
import { CLASS_COLORS } from "@/lib/constants";
import { getNegativeOverview, sendDiscordWebhook, dismissIssue, restoreIssue } from "@/lib/api";
import { toast } from "sonner";

const ISSUE_ICONS = {
  missing_enchant: Skull,
  missing_gems: Diamond,
  no_consumables: FlaskConical,
  low_consumables: FlaskConical,
  missing_flask: FlaskConical,
  missing_food: FlaskConical,
  low_uptime: FlaskConical,
  gear_issue: Shield,
  low_parse: TrendingDown,
  deaths: Skull,
  friendly_fire: Crosshair,
  death_by_avoidable: Zap,
  upgrade_needed: Flag,
  spell_pen: Shield,
};

function IssueTag({ issue, onAction, actionType, screenshotMode }) {
  const Icon = ISSUE_ICONS[issue.type] || AlertTriangle;
  const isDismissed = issue.dismissed;
  return (
    <div className={`flex items-center gap-1.5 text-sm group ${isDismissed ? "opacity-40 line-through" : ""}`}>
      <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${isDismissed ? "text-muted-foreground" : "text-destructive"}`} />
      <span className={isDismissed ? "text-muted-foreground" : "text-foreground/80"}>{issue.detail}</span>
      {!screenshotMode && onAction && (
        <button
          onClick={() => onAction(issue)}
          className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
          data-testid={`${actionType}-issue-btn`}
        >
          {actionType === "dismiss" ? (
            <X className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
          ) : (
            <Undo2 className="h-3.5 w-3.5 text-muted-foreground hover:text-green-500" />
          )}
        </button>
      )}
    </div>
  );
}

function PlayerIssueCard({ item, webhookUrl, raidId, screenshotMode, filteredIssues, onPlayerClick, onDismiss, onRestore, showDismissed }) {
  const classColor = CLASS_COLORS[item.wow_class] || CLASS_COLORS.Unknown;
  const [sending, setSending] = useState(false);
  const displayIssues = filteredIssues || item.issues;
  const dismissedIssues = item.dismissed_issues || [];

  const handleSendPlayer = async () => {
    if (!webhookUrl) {
      toast.error("Set your Discord Webhook URL first (top-right send button)");
      return;
    }
    setSending(true);
    try {
      await sendDiscordWebhook(webhookUrl, raidId, item.player_name);
      toast.success(`Sent ${item.player_name} to Discord`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    }
    setSending(false);
  };

  return (
    <div className="stat-card" data-testid={`neg-player-${item.player_name}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: classColor }} />
        <span
          className="text-sm font-bold cursor-pointer hover:underline"
          style={{ color: classColor }}
          onClick={() => onPlayerClick && item.player_id && onPlayerClick(item.player_id)}
          data-testid={`neg-player-link-${item.player_name}`}
        >
          {item.player_name}
        </span>
        <Badge variant="outline" className="text-xs font-mono ml-auto">
          {displayIssues.length} issue(s)
        </Badge>
      </div>
      <div className="space-y-1 mb-3">
        {displayIssues.map((issue, idx) => (
          <IssueTag
            key={idx}
            issue={issue}
            onAction={onDismiss ? () => onDismiss(item.player_id, issue) : undefined}
            actionType="dismiss"
            screenshotMode={screenshotMode}
          />
        ))}
        {showDismissed && dismissedIssues.length > 0 && (
          <>
            <div className="border-t border-border/20 mt-2 pt-2 mb-1">
              <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                Dismissed ({dismissedIssues.length})
              </span>
            </div>
            {dismissedIssues.map((issue, idx) => (
              <IssueTag
                key={`d-${idx}`}
                issue={issue}
                onAction={onRestore ? () => onRestore(item.player_id, issue) : undefined}
                actionType="restore"
                screenshotMode={screenshotMode}
              />
            ))}
          </>
        )}
      </div>
      {!screenshotMode && (
        <Button
          data-testid={`send-discord-${item.player_name}`}
          variant="outline"
          size="sm"
          onClick={handleSendPlayer}
          disabled={sending}
          className="w-full text-xs border-[#5865F2]/40 text-[#5865F2] hover:bg-[#5865F2]/10 rounded-sm"
        >
          {sending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Send className="h-3 w-3 mr-1" />}
          {sending ? "Sending..." : "Send to Discord"}
        </Button>
      )}
    </div>
  );
}

export default function NegativeOverview({ raids, team }) {
  const navigate = useNavigate();
  const [selectedRaid, setSelectedRaid] = useState("");
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [screenshotMode, setScreenshotMode] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState(() => localStorage.getItem("discord_webhook_url") || "");
  const [sending, setSending] = useState(false);
  const [webhookOpen, setWebhookOpen] = useState(false);
  const [showDismissed, setShowDismissed] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const raidParam = params.get("raid");
    if (raidParam) setSelectedRaid(raidParam);
  }, []);

  useEffect(() => {
    if (!selectedRaid) return;
    const load = async () => {
      setLoading(true);
      try {
        const res = await getNegativeOverview(selectedRaid);
        setOverview(res);
      } catch (e) {
        toast.error("Failed to load overview");
      }
      setLoading(false);
    };
    load();
  }, [selectedRaid]);

  const handleSendAll = async () => {
    setSending(true);
    try {
      await sendDiscordWebhook(webhookUrl, selectedRaid);
      toast.success("All players sent to Discord!");
      setWebhookOpen(false);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 401) {
        toast.error("Sign in first to send to Discord");
      } else if (status === 403) {
        toast.error("Officer or admin access required");
      } else {
        toast.error(detail || "Failed to send");
      }
    }
    setSending(false);
  };

  const handleDismiss = async (playerId, issue) => {
    try {
      await dismissIssue(selectedRaid, playerId, issue.type, issue.detail);
      const res = await getNegativeOverview(selectedRaid);
      setOverview(res);
      toast.success("Issue dismissed");
    } catch (e) {
      toast.error("Failed to dismiss");
    }
  };

  const handleRestore = async (playerId, issue) => {
    try {
      await restoreIssue(selectedRaid, playerId, issue.type, issue.detail);
      const res = await getNegativeOverview(selectedRaid);
      setOverview(res);
      toast.success("Issue restored");
    } catch (e) {
      toast.error("Failed to restore");
    }
  };


  return (
    <div className={`p-6 md:p-8 lg:p-12 animate-fade-in ${screenshotMode ? "screenshot-mode" : ""}`}
         data-testid="negative-overview-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="heading text-4xl md:text-5xl font-bold tracking-tight uppercase text-destructive">
            Negative Overview
          </h1>
          <p className="text-muted-foreground mt-1">
            Issues across all raiders — fix these before next week
          </p>
        </div>
        <div className="flex gap-2">
          {/* Discord Webhook Config + Send All — hidden in screenshot mode */}
          {!screenshotMode && (
            <Dialog open={webhookOpen} onOpenChange={setWebhookOpen}>
              <DialogTrigger asChild>
                <Button data-testid="discord-webhook-btn" variant="outline" size="sm"
                  className="border-[#5865F2]/50 text-[#5865F2] hover:bg-[#5865F2]/10 gap-1.5"
                  disabled={!selectedRaid || !overview}>
                  <Send className="h-3.5 w-3.5" /> Send All to Discord
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md bg-background">
                <DialogHeader>
                  <DialogTitle className="heading text-lg">Send to Discord</DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1.5 block">
                      Webhook URL
                    </label>
                    <Input data-testid="webhook-url-input" value={webhookUrl}
                      onChange={(e) => { setWebhookUrl(e.target.value); localStorage.setItem("discord_webhook_url", e.target.value); }}
                      placeholder="https://discord.com/api/webhooks/..." className="bg-card/50" />
                    <p className="text-xs text-muted-foreground mt-1">
                      Server Settings &gt; Integrations &gt; Webhooks &gt; New Webhook
                    </p>
                  </div>
                  <div className="flex justify-end gap-2">
                    <DialogClose asChild>
                      <Button variant="outline" size="sm" className="text-xs rounded-sm">Cancel</Button>
                    </DialogClose>
                    <Button data-testid="send-webhook-btn" size="sm" disabled={!webhookUrl || sending}
                      onClick={handleSendAll}
                      className="rounded-sm font-bold uppercase tracking-wider text-xs bg-[#5865F2] text-white hover:bg-[#4752C4]">
                      <Send className="h-3.5 w-3.5 mr-1" /> {sending ? "Sending..." : "Send All"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}

          {!screenshotMode && (
            <Button data-testid="neg-show-dismissed-btn" variant="ghost" size="icon"
              onClick={() => setShowDismissed(!showDismissed)}
              className={showDismissed ? "bg-accent" : ""}
              title={showDismissed ? "Hide dismissed" : "Show dismissed"}>
              {showDismissed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
          )}

          <Button data-testid="neg-screenshot-btn" variant="ghost" size="icon"
            onClick={() => setScreenshotMode(!screenshotMode)}
            className={screenshotMode ? "bg-accent" : ""}>
            <Camera className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Webhook URL inline config — hidden in screenshot mode */}
      {!screenshotMode && (
        <div className="mb-4 flex gap-2 items-end max-w-lg">
          <div className="flex-1">
            <label className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1 block">
              Discord Webhook URL (saved locally)
            </label>
            <Input
              data-testid="webhook-url-inline"
              value={webhookUrl}
              onChange={(e) => { setWebhookUrl(e.target.value); localStorage.setItem("discord_webhook_url", e.target.value); }}
              placeholder="https://discord.com/api/webhooks/..."
              className="bg-card/50 text-xs h-8"
            />
          </div>
        </div>
      )}

      {/* Raid Selector — hidden in screenshot mode */}
      {!screenshotMode && (
        <div className="mb-6 max-w-md">
          <label className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1.5 block">
            Select Raid Log
          </label>
          <Select value={selectedRaid} onValueChange={setSelectedRaid}>
            <SelectTrigger data-testid="neg-raid-selector" className="bg-card/50">
              <SelectValue placeholder="Choose a raid..." />
            </SelectTrigger>
            <SelectContent>
              {raids.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {r.raid_name} — {r.date}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {!selectedRaid && (
        <Card className="bg-card/50 border-border">
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">Select a raid to view the negative overview.</p>
          </CardContent>
        </Card>
      )}

      {loading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="stat-card animate-pulse h-20" />
          ))}
        </div>
      )}

      {overview && !loading && (
        <>
          <div className="flex items-center gap-4 mb-6 p-3 bg-destructive/10 border border-destructive/30 rounded-sm">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <span className="text-sm font-semibold">
              {overview.issues.filter(i => i.issues.length > 0).length} player(s) with issues
            </span>
            <span className="text-xs text-muted-foreground">
              {overview.raid?.raid_name} — {overview.raid?.date}
            </span>
            {overview.issues.some(i => i.dismissed_issues?.length > 0) && (
              <span className="text-xs text-muted-foreground ml-auto">
                {overview.issues.reduce((sum, i) => sum + (i.dismissed_issues?.length || 0), 0)} dismissed
              </span>
            )}
          </div>

          {overview.issues.filter(i => i.issues.length > 0 || (showDismissed && i.dismissed_issues?.length > 0)).length === 0 ? (
            <Card className="bg-card/50 border-green-500/30">
              <CardContent className="py-12 text-center">
                <p className="text-green-500 font-semibold">All clear! No issues found.</p>
              </CardContent>
            </Card>
          ) : screenshotMode ? (
            /* ── Screenshot Mode: Two sections ── */
            (() => {
              const gearIssues = overview.issues
                .map(item => ({
                  ...item,
                  filteredIssues: item.issues.filter(i => i.type !== "friendly_fire" && i.type !== "death_by_avoidable")
                }))
                .filter(item => item.filteredIssues.length > 0)
                .sort((a, b) => b.filteredIssues.length - a.filteredIssues.length);

              const avoidableItems = overview.issues
                .filter(item => item.issues.some(i => i.type === "friendly_fire" || i.type === "death_by_avoidable"))
                .sort((a, b) => {
                  const score = (it) => it.issues.filter(i => i.type === "friendly_fire" || i.type === "death_by_avoidable").length;
                  return score(b) - score(a);
                });

              return (
                <div className="space-y-6">
                  {/* Section 1: Gear, Enchants, Gems, Consumables, Performance */}
                  {gearIssues.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-destructive/30">
                        <Skull className="h-4 w-4 text-destructive" />
                        <h2 className="text-sm font-bold uppercase tracking-wider text-destructive">
                          Gear, Enchants & Consumables
                        </h2>
                        <Badge variant="outline" className="text-xs font-mono ml-auto text-destructive border-destructive/40">
                          {gearIssues.length} player(s)
                        </Badge>
                      </div>
                      <div className="grid grid-cols-1 gap-2">
                        {gearIssues.map((item) => (
                          <PlayerIssueCard
                            key={item.player_name}
                            item={item}
                            webhookUrl={webhookUrl}
                            raidId={selectedRaid}
                            screenshotMode={true}
                            filteredIssues={item.filteredIssues}
                            onPlayerClick={(id) => navigate(`/player/${id}`)}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Section 2: Friendly Fire & Deaths by Avoidable */}
                  {avoidableItems.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-3 pb-2 border-b border-orange-500/30">
                        <Zap className="h-4 w-4 text-orange-400" />
                        <h2 className="text-sm font-bold uppercase tracking-wider text-orange-400">
                          Friendly Fire & Avoidable Deaths
                        </h2>
                        <Badge variant="outline" className="text-xs font-mono ml-auto text-orange-400 border-orange-400/40">
                          {avoidableItems.length} player(s)
                        </Badge>
                      </div>
                      <div className="stat-card p-2">
                        {avoidableItems.map((item) => {
                          const classColor = CLASS_COLORS[item.wow_class] || CLASS_COLORS.Unknown;
                          const ffIssue = item.issues.find(i => i.type === "friendly_fire");
                          const adIssue = item.issues.find(i => i.type === "death_by_avoidable");
                          return (
                            <div key={item.player_name} className="flex items-center gap-3 py-1.5 px-2 border-b border-border/30 last:border-0">
                              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: classColor }} />
                              <span
                                className="text-sm font-semibold min-w-[120px] cursor-pointer hover:underline"
                                style={{ color: classColor }}
                                onClick={() => item.player_id && navigate(`/player/${item.player_id}`)}
                              >
                                {item.player_name}
                              </span>
                              <div className="flex gap-3 text-xs">
                                {ffIssue && <span className="text-red-400">{ffIssue.detail}</span>}
                                {adIssue && <span className="text-orange-400">{adIssue.detail}</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {overview.issues
                .filter(item => item.issues.length > 0 || (showDismissed && item.dismissed_issues?.length > 0))
                .map((item) => (
                <PlayerIssueCard
                  key={item.player_name}
                  item={item}
                  webhookUrl={webhookUrl}
                  raidId={selectedRaid}
                  onPlayerClick={(id) => navigate(`/player/${id}`)}
                  onDismiss={handleDismiss}
                  onRestore={handleRestore}
                  showDismissed={showDismissed}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
